import { Button, IconButton, Stack } from '@mui/material';
import { PlusIcon, TrashIcon } from '@phosphor-icons/react';
import * as uuid from 'uuid';

import { Text } from 'common';

import { BlockValueTemplateField } from '../../../ConfigFields/CodeMirror/FlowTemplate';
import { useSelectedNodeField } from '../../../ConfigFields/useSelectedNodeField';

interface OrderProceduresProps {
  prefix?: string;
}

export function OrderProcedures({ prefix = 'config.payload' }: OrderProceduresProps) {
  const { value: procedures, setValue: setProcedures } = useSelectedNodeField<
    | {
        procedureId: string;
        description: string;
        cptCode: string | null;
        areaOfInterest: string | null;
      }[]
    | string
    | undefined
  >(`${prefix}.procedures`);

  const showTextField = !procedures || typeof procedures === 'string' || procedures.length === 0;
  const showArrayEditor = !procedures || Array.isArray(procedures);

  return (
    <Stack gap={3}>
      <Text variant="heading5" bold>
        Procedures
      </Text>
      {showTextField && (
        <BlockValueTemplateField
          fieldName={`${prefix}.procedures`}
          label="Procedures"
          helperText="Enter the reference to procedures"
        />
      )}
      {showArrayEditor && (
        <>
          {Array.isArray(procedures) &&
            procedures?.map((procedure, index) => {
              const fieldNamePrefix = `${prefix}.procedures.${index}`;
              return (
                <Stack key={procedure.procedureId} spacing={2} padding={1} paddingLeft={2}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Text variant="heading5" bold>
                      Procedure {index + 1}
                    </Text>
                    <IconButton
                      aria-label="Delete Field"
                      size="medium"
                      onClick={() => {
                        const newProcedures = procedures.filter((_, i) => i !== index);
                        if (newProcedures.length === 0) {
                          setProcedures(undefined);
                        } else {
                          setProcedures(procedures.filter((_, i) => i !== index));
                        }
                      }}
                    >
                      <TrashIcon size={16} />
                    </IconButton>
                  </Stack>
                  <BlockValueTemplateField
                    fieldName={`${fieldNamePrefix}.description`}
                    label="Description"
                    helperText="Enter the reference to the procedure's Description. e.g. `{{data.description}}`"
                    placeholder="e.g. {{data.description}}"
                  />
                  <BlockValueTemplateField
                    fieldName={`${fieldNamePrefix}.cptCode`}
                    label="Cpt Code"
                    helperText="Enter the reference to the procedure's Cpt Code. e.g. `{{data.cptCode}}`"
                    placeholder="e.g. {{data.cptCode}}"
                  />
                  <BlockValueTemplateField
                    fieldName={`${fieldNamePrefix}.areaOfInterest`}
                    label="Area Of Interest"
                    helperText="Enter the reference to the procedure's Area Of Interest. e.g. `{{data.areaOfInterest}}`"
                    placeholder="e.g. {{data.areaOfInterest}}"
                  />
                </Stack>
              );
            })}
          <Button
            variant="contained"
            onClick={() => {
              const newProcedure = {
                procedureId: uuid.v4(),
                description: '',
                cptCode: '',
                areaOfInterest: '',
              };
              if (Array.isArray(procedures)) {
                setProcedures([...(procedures ?? []), newProcedure]);
              } else {
                // FIXME - unsure what to do if it is a string
                setProcedures([newProcedure]);
              }
            }}
          >
            <PlusIcon size={12} />
            Add Procedure
          </Button>
        </>
      )}
    </Stack>
  );
}
